import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FiCheckCircle, FiAlertCircle, FiLock, FiCheck, FiX } from 'react-icons/fi';
import actLogo from '../assets/actlogo.png';

const ResetPasswordPage = () => {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [errorDetails, setErrorDetails] = useState('');
    const [loading, setLoading] = useState(false);
    const [tokenExpired, setTokenExpired] = useState(false);
    const [tokenValid, setTokenValid] = useState(true);
    const [passwordRequirements, setPasswordRequirements] = useState({
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false
    });
    const navigate = useNavigate();

    useEffect(() => {
        // Check password requirements in real-time
        checkPasswordStrength(password);
    }, [password]);

    useEffect(() => {
        // Validate token on component mount
        const validateToken = async () => {
            const token = new URLSearchParams(window.location.search).get('token');
            const email = new URLSearchParams(window.location.search).get('email');

            if (!token || !email) {
                setTokenValid(false);
                setError('Invalid or missing reset parameters');
                setErrorDetails('The password reset link appears to be invalid. Please request a new password reset.');
                return;
            }

            // Optional: you could add a token validation API call here
        };

        validateToken();
    }, []);

    // Utility function to validate email format
    const validateEmail = (email) => {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(String(email).toLowerCase());
    };

    // Utility function to validate password strength
    const validatePassword = (password) => {
        const minLength = 8;
        const hasUpperCase = /[A-Z]/.test(password);
        const hasLowerCase = /[a-z]/.test(password);
        const hasNumbers = /[0-9]/.test(password);
        const hasNonalphas = /[\W]/.test(password);
        return (
            password.length >= minLength &&
            hasUpperCase &&
            hasLowerCase &&
            hasNumbers &&
            hasNonalphas
        );
    };

    const checkPasswordStrength = (password) => {
        setPasswordRequirements({
            length: password.length >= 8,
            uppercase: /[A-Z]/.test(password),
            lowercase: /[a-z]/.test(password),
            number: /[0-9]/.test(password),
            special: /[\W]/.test(password)
        });
    };

    // Handle form submission for password reset
    const handlePasswordReset = async (event) => {
        event.preventDefault();
        setLoading(true);
        setError('');
        setErrorDetails('');
        setMessage('');

        const token = new URLSearchParams(window.location.search).get('token');
        const email = new URLSearchParams(window.location.search).get('email');

        if (!token || !email) {
            setError('Invalid or missing token');
            setErrorDetails('The password reset link appears to be invalid. Please request a new password reset.');
            setLoading(false);
            return;
        }

        if (!validateEmail(email)) {
            setError('Invalid email format');
            setErrorDetails('The email address in the reset link is improperly formatted.');
            setLoading(false);
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            setErrorDetails('Please ensure both password fields contain the same value.');
            setLoading(false);
            return;
        }

        if (!validatePassword(password)) {
            setError('Password requirements not met');
            setErrorDetails(
                'Your password must meet all the security requirements listed below.'
            );
            setLoading(false);
            return;
        }

        try {
            const response = await axios.post('/api/xapi', {
                action: 'resetPassword',
                email,
                password,
                token,
            });

            if (response.data.message === 'Password reset successful') {
                setMessage('Password reset successful! You can now log in with your new password.');
                setError('');
                setErrorDetails('');
                
                // Automatically redirect to login page after 3 seconds
                setTimeout(() => {
                    navigate('/login');
                }, 3000);
            } else if (response.data.message === 'Token expired') {
                setError('Reset link expired');
                setErrorDetails('The reset link has expired. Please request a new password reset.');
                setTokenExpired(true);
            } else {
                setError('Reset failed');
                setErrorDetails('Failed to reset password. Please try again or request a new reset link.');
            }
        } catch (error) {
            console.error('Error resetting password:', error);
            
            if (error.response && error.response.data) {
                if (error.response.data.message === 'Token expired') {
                    setError('Reset link expired');
                    setErrorDetails('The reset link has expired. Please request a new password reset.');
                    setTokenExpired(true);
                } else if (error.response.data.message === 'Invalid reset token') {
                    setError('Invalid reset link');
                    setErrorDetails('This password reset link is invalid. Please request a new one.');
                    setTokenValid(false);
                } else {
                    setError('Error resetting password');
                    setErrorDetails(error.response.data.message || 'Please try again later.');
                }
            } else {
                setError('Connection error');
                setErrorDetails('Unable to connect to the server. Please check your internet connection and try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleRequestNewReset = () => {
        navigate('/login');
    }

    return (
        <div className="flex items-center justify-center min-h-screen relative overflow-hidden"
             style={{ background: 'linear-gradient(135deg, #0077B6 0%, #03045E 100%)' }}>
            <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="bg-white p-6 md:p-8 rounded-2xl shadow-card w-full max-w-md mx-4 md:mx-0 relative z-10"
            >
                <div className="text-center mb-6">
                    <motion.img 
                        src={actLogo} 
                        alt="ACT Logo" 
                        className="mx-auto w-28 md:w-32 mb-4" 
                        initial={{ scale: 0.8 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.3 }}
                    />
                    <motion.h2 
                        className="text-2xl md:text-3xl font-bold mb-2 text-brandDark"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.2, duration: 0.5 }}
                    >
                        Reset Your Password
                    </motion.h2>
                    <motion.p
                        className="text-gray-600 text-sm md:text-base"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3, duration: 0.5 }}
                    >
                        Create a new secure password for your account
                    </motion.p>
                </div>
                
                {message && (
                    <motion.div 
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="bg-green-50 border-l-4 border-success p-4 mb-6 rounded-md flex items-center"
                    >
                        <FiCheckCircle className="text-success mr-2 text-xl flex-shrink-0" />
                        <div>
                            <p className="text-success">{message}</p>
                            <p className="text-sm text-success mt-1">Redirecting to login page...</p>
                        </div>
                    </motion.div>
                )}

                {error && (
                    <motion.div 
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="bg-red-50 border-l-4 border-danger p-4 mb-6 rounded-md flex flex-col"
                    >
                        <div className="flex items-center">
                            <FiAlertCircle className="text-danger mr-2 flex-shrink-0" />
                            <span className="text-danger font-medium">{error}</span>
                        </div>
                        {errorDetails && (
                            <span className="text-danger text-sm mt-1 ml-6">{errorDetails}</span>
                        )}
                        
                        {(tokenExpired || !tokenValid) && (
                            <motion.button
                                onClick={handleRequestNewReset}
                                className="bg-danger text-white mt-3 py-2 px-4 rounded-md text-sm inline-block ml-6 hover:bg-red-600"
                                whileHover={{ scale: 1.03 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                Request New Reset Link
                            </motion.button>
                        )}
                    </motion.div>
                )}

                {tokenValid && !tokenExpired && !message && (
                    <form onSubmit={handlePasswordReset} className="space-y-4">
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">New Password</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <FiLock className="text-gray-400" />
                                </div>
                                <input
                                    type="password"
                                    placeholder="Create new password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-brandBlue focus:border-brandBlue"
                                />
                            </div>
                            
                            {/* Password strength requirements */}
                            <div className="mt-2 space-y-1">
                                <p className="text-xs font-medium text-gray-700">Password must include:</p>
                                <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-2 gap-y-1">
                                    <li className={`text-xs flex items-center ${passwordRequirements.length ? 'text-success' : 'text-gray-500'}`}>
                                        {passwordRequirements.length ? <FiCheck className="mr-1" /> : <FiX className="mr-1" />}
                                        At least 8 characters
                                    </li>
                                    <li className={`text-xs flex items-center ${passwordRequirements.uppercase ? 'text-success' : 'text-gray-500'}`}>
                                        {passwordRequirements.uppercase ? <FiCheck className="mr-1" /> : <FiX className="mr-1" />}
                                        Uppercase letter
                                    </li>
                                    <li className={`text-xs flex items-center ${passwordRequirements.lowercase ? 'text-success' : 'text-gray-500'}`}>
                                        {passwordRequirements.lowercase ? <FiCheck className="mr-1" /> : <FiX className="mr-1" />}
                                        Lowercase letter
                                    </li>
                                    <li className={`text-xs flex items-center ${passwordRequirements.number ? 'text-success' : 'text-gray-500'}`}>
                                        {passwordRequirements.number ? <FiCheck className="mr-1" /> : <FiX className="mr-1" />}
                                        Number
                                    </li>
                                    <li className={`text-xs flex items-center ${passwordRequirements.special ? 'text-success' : 'text-gray-500'}`}>
                                        {passwordRequirements.special ? <FiCheck className="mr-1" /> : <FiX className="mr-1" />}
                                        Special character
                                    </li>
                                </ul>
                            </div>
                        </div>
                        
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">Confirm Password</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <FiLock className="text-gray-400" />
                                </div>
                                <input
                                    type="password"
                                    placeholder="Confirm new password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-brandBlue focus:border-brandBlue"
                                />
                            </div>
                            
                            {/* Password match indicator */}
                            {password && confirmPassword && (
                                <div className="mt-1 flex items-center">
                                    {password === confirmPassword ? (
                                        <p className="text-xs text-success flex items-center">
                                            <FiCheck className="mr-1" /> Passwords match
                                        </p>
                                    ) : (
                                        <p className="text-xs text-danger flex items-center">
                                            <FiX className="mr-1" /> Passwords don't match
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                        
                        <motion.button 
                            type="submit" 
                            disabled={loading}
                            className="w-full py-2 px-4 mt-2 bg-brandBlue text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-400 flex items-center justify-center"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner mr-2"></span>
                                    Processing...
                                </>
                            ) : (
                                'Reset Password'
                            )}
                        </motion.button>
                    </form>
                )}
                
                <div className="mt-6 text-center">
                    <motion.a 
                        href="/login"
                        className="text-brandBlue hover:underline text-sm"
                        whileHover={{ scale: 1.05 }}
                    >
                        Return to Login
                    </motion.a>
                </div>
            </motion.div>
        </div>
    );
};

export default ResetPasswordPage;
